import { publicEnv } from '@/lib/config/public-env';
/**
 * Constants for StudioAI feature
 */

import { PlanTemplateStepType } from '@/@studio-ai/types';

export const STUDIO_AI_NAME = 'DANI';
export const NEW_CONVERSATION_ID = 'new';

export const TEMP_MESSAGE_IDS = {
    PENDING_USER: 'pending-user-id',
};

export enum UserFeedbackFilter {
    ALL = 'all',
    UNREAD = 'unread',
    HAS_RESPONSE = 'hasResponse',
    NO_RESPONSE = 'noResponse',
    READ = 'read',
}

export const API_ENDPOINTS = {
    CONVERSATIONS: '/studio-ai/conversations',
    CONVERSATION_HISTORY: '/studio-ai/conversations',
    DELETE_CONVERSATION: '/studio-ai/conversations',
    FEEDBACK: '/studio-ai/feedback',
    DATA_REFRESH_DATES: '/studio-ai/data-refresh-dates',
    CHAT: '/studio-ai/chat',
    PLAN_MODE_CHAT: '/studio-ai/plan-mode/chat',
    FREEMIUM_CHAT: '/studio-ai/freemium/chat',
    FREEMIUM_CONVERSATION: '/studio-ai/freemium/conversations',
    ACTIVE_CONFIG: '/studio-ai/active-config',
    FREEMIUM_ACTIVE_CONFIG: '/studio-ai/freemium/active-config',
    FOLDERS: '/studio-ai/folders',
    SHARE_CONVERSATION: '/studio-ai/conversations',
    SHARED_WITH_ME: '/studio-ai/shared-with-me',
    ORGANIZATION_USERS: '/studio-ai/organization/users',
    SHARES: '/studio-ai/shares',
    RELEASE_NOTES_ALL: '/studio-ai/release-notes/all',
    RELEASE_NOTES_PENDING: '/studio-ai/release-notes/pending',
    RELEASE_NOTES_ACKNOWLEDGE: '/studio-ai/release-notes/acknowledge',
    RELEASE_NOTES_DISMISS: '/studio-ai/release-notes/dismiss',
    ACCESS_STATUS: '/studio-ai/access-status',
    EXPORT_QUERY_DATA: '/studio-ai/export-query-data',
    TRANSCRIBE: '/studio-ai/transcribe',
    REPORTS: '/studio-ai/reports',
    STOP_CONVERSATION: '/studio-ai/conversations',
    PLAN_TEMPLATES: '/studio-ai/plan-templates',
    ADMIN_PLAN_TEMPLATES: '/studio-ai/admin/plan-templates',
    ADMIN_PLAN_TEMPLATE_PROMPTS_FROM_CONVERSATION:
        '/studio-ai/admin/plan-templates/from-conversation',
    ADMIN_TEMPLATE_STYLES: '/studio-ai/admin/template-styles',
} as const;

// Voice input
export const VOICE_MIN_AUDIO_BYTES = 1_000;
export const VOICE_MAX_AUDIO_BYTES = 10 * 1024 * 1024; // 10 MB
export const VOICE_MAX_RECORDING_DURATION_MS = 5 * 60 * 1000; // 5 minutes

export const CONVERSATIONS_PAGE_SIZE = 50;
export const CONVERSATIONS_PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

export const QUERY_KEYS = {
    CONVERSATIONS: (params?: {
        limit?: number;
        search?: string;
        page?: number;
        folderId?: number;
    }) =>
        [
            'studio-ai',
            'conversations',
            params?.limit ?? 50,
            params?.search ?? '',
            params?.page ?? 1,
            params?.folderId ?? null,
        ] as const,
    CONVERSATION_HISTORY: (conversationId: string) =>
        ['studio-ai', 'conversation', conversationId] as const,
    FEEDBACK_COUNT: ['studio-ai', 'feedback-count'] as const,
    USER_FEEDBACK: (filter?: UserFeedbackFilter) =>
        ['studio-ai', 'user-feedback', filter || UserFeedbackFilter.ALL] as const,
    DATA_REFRESH_DATES: ['studio-ai', 'data-refresh-dates'] as const,
    ACTIVE_CONFIG: ['studio-ai', 'active-config'] as const,
    FOLDERS: ['studio-ai', 'folders'] as const,
    SHARED_WITH_ME: ['studio-ai', 'shared-with-me'] as const,
    ORGANIZATION_USERS: ['studio-ai', 'organization-users'] as const,
    CONVERSATION_SHARES: (conversationId: string) =>
        ['studio-ai', 'conversation-shares', conversationId] as const,
    RELEASE_NOTES_ALL: (params?: {
        page?: number;
        limit?: number;
        search?: string;
        filter?: 'all' | 'new' | 'acknowledged' | 'internal';
        area?: string;
    }) =>
        [
            'studio-ai',
            'release-notes',
            'all',
            params?.page ?? 1,
            params?.limit ?? 10,
            params?.search ?? '',
            params?.filter ?? 'all',
            params?.area ?? '',
        ] as const,
    RELEASE_NOTES_PENDING: ['studio-ai', 'release-notes', 'pending'] as const,
    ACCESS_STATUS: ['studio-ai', 'access-status'] as const,
    REPORTS: (params?: { limit?: number; search?: string; page?: number; folderId?: number }) =>
        [
            'studio-ai',
            'reports',
            params?.limit ?? 50,
            params?.search ?? '',
            params?.page ?? 1,
            params?.folderId ?? null,
        ] as const,
    REPORT_HTML: (reportId: number) => ['studio-ai', 'report-html', reportId] as const,
    REPORT_SLIDES: (reportId: number) => ['studio-ai', 'report-slides', reportId] as const,
    PLAN_TEMPLATES: ['studio-ai', 'plan-templates'] as const,
    ADMIN_PLAN_TEMPLATES: (params?: { page?: number; limit?: number }) =>
        ['studio-ai', 'admin-plan-templates', params?.page ?? 1, params?.limit ?? 50] as const,
    ADMIN_TEMPLATE_STYLES: (params?: { page?: number; limit?: number; visibleOnly?: boolean }) =>
        [
            'studio-ai',
            'admin-template-styles',
            params?.page ?? 1,
            params?.limit ?? 50,
            params?.visibleOnly ?? false,
        ] as const,
    ADMIN_TEMPLATE_STYLES_ALL: (params?: { visibleOnly?: boolean }) =>
        ['studio-ai', 'admin-template-styles', 'all', params?.visibleOnly ?? false] as const,
} as const;

export const CHAT_STATES = {
    IDLE: 'idle',
    LOADING: 'loading',
    STREAMING: 'streaming',
    EXECUTING_SQL: 'executing_sql',
    RUNNING_ANALYTICS: 'running_analytics',
    ERROR: 'error',
} as const;

export const DEFAULT_MESSAGES = {
    WELCOME: 'What consumer connections can I help you explore and discover?',
    ERROR: 'Sorry, it looks like something went wrong. Please try again.',
    EMPTY_CONVERSATIONS: 'No chat conversations yet. Start a new conversation!',
    CANCELLED: '(Message cancelled)',
    /** A stream that ended with nothing to show: an idle timeout, a connection that closed
     * without `done`, or an empty `done` (BT-2067). */
    NO_ANSWER:
        'DANI stopped before showing an answer. Very large results can take too long to display here: ask again with a narrower question, or use the download arrow on a table to get the full data.',
    INFO_WARNING:
        'This dataset remains in an active training and validation phase while confidence thresholds are being calibrated.',
    LIMIT_REACHED: `You have reached your message limit for the month. To unlock full access to ${STUDIO_AI_NAME}, please contact our sales team at [${publicEnv('NEXT_PUBLIC_SALES_EMAIL')}](mailto:${publicEnv('NEXT_PUBLIC_SALES_EMAIL')}). We'd love to help you get the most out of your data.`,
} as const;

export const CHAT_INPUT_MESSAGES = {
    MAIN: `Hello, I'm ${STUDIO_AI_NAME}, your Data Analytics and Navigation Instructor.`,
    MAIN_SUBTITLE: 'What consumer connections can I help you explore and discover?',
    PLACEHOLDER: `Hi ${STUDIO_AI_NAME}, what insights can you give me?`,
    UPLOAD_DOCUMENT_LABEL: 'Attach document',
    DEEP_RESEARCH_LABEL: 'Deep Research mode',
};

export const PLANNER_MODE_MESSAGES = {
    WELCOME: `Planning Mode`,
    WELCOME_SUBTITLE: 'What would you like this report to be about?',
    WELCOME_MESSAGE: `Hi! I'm ${STUDIO_AI_NAME} in Planning Mode. I'll help you build a custom research report.\n\nWhat brand would you like to create a report for?`,
    PLACEHOLDER: 'Describe the report you want to create...',
    BUILDING_PLAN: 'Building your report plan...',
    ACTIVATING_STATUS: {
        message: 'Starting the planning workflow',
        icon: 'lucide:layout-template',
    },
} as const;

export const ALLOWED_DOCUMENT_TYPES = ['pdf', 'xlsx', 'csv', 'txt', 'docx', 'doc'] as const;
export const DOCUMENT_UPLOAD_NAME = 'document';

export const UI_CONSTANTS = {
    MAX_MESSAGE_LENGTH: 10000,
    SIDEBAR_WIDTH: 320,
} as const;

export const UI_VARIANTS = {
    TEXT_DISPLAY: {
        BUBBLE: 'bubble',
        PLAIN: 'plain',
    },
    DETAIL_TYPE: {
        THINKING: 'thinking',
        SQL: 'sql',
        TOOL_USE: 'tool_use',
    },
} as const;

export const STATUS_MESSAGES = {
    CREATING_THREAD: { message: 'Getting the conversation ready', icon: 'lucide:loader-2' },
    THINKING: { message: 'Thinking about your question', icon: 'lucide:brain' },
    FINDING_DATA: { message: 'Finding the right data sources', icon: 'lucide:database' },
    UNDERSTANDING_REQUEST: { message: 'Understanding your request', icon: 'lucide:search' },
    WRITING_QUERY: { message: 'Writing the query', icon: 'lucide:code' },
    OPTIMIZING_QUERY: { message: 'Optimizing the query', icon: 'lucide:zap' },
    GATHERING_DATA: { message: 'Gathering data', icon: 'lucide:play-circle' },
    ANALYZING_RESULTS: { message: 'Analyzing the results', icon: 'lucide:bar-chart-3' },
    REFINING_APPROACH: { message: 'Refining the approach', icon: 'lucide:refresh-cw' },
    PREPARING_ANSWER: { message: 'Preparing your answer', icon: 'lucide:edit-3' },
    EXECUTING_SQL: { message: 'Executing SQL', icon: 'lucide:database' },
    RUNNING_ANALYTICS: { message: 'Analyzing data...', icon: 'lucide:bar-chart-3' },
    PROCESSING: { message: 'Thinking about your answer', icon: 'lucide:loader-2' },
    PLANNING: { message: 'Planning the next steps', icon: 'lucide:layout-template' },
} as const;

export enum DispatchType {
    AddMessage = 'ADD_MESSAGE',
    UpdateMessage = 'UPDATE_MESSAGE',
    SetMessages = 'SET_MESSAGES',
    SetConversations = 'SET_CONVERSATIONS',
    AddConversation = 'ADD_CONVERSATION',
    UpdateConversation = 'UPDATE_CONVERSATION',
    RemoveConversation = 'REMOVE_CONVERSATION',
    SetChatState = 'SET_CHAT_STATE',
    SetError = 'SET_ERROR',
    SetLoading = 'SET_LOADING',
    SetCurrentStatus = 'SET_CURRENT_STATUS',
    SetThreadId = 'SET_THREAD_ID',
    SetParentMessageId = 'SET_PARENT_MESSAGE_ID',
    SetPlanProgress = 'SET_PLAN_PROGRESS',
    SetConversationContextData = 'SET_CONVERSATION_CONTEXT_DATA',
    ClearError = 'CLEAR_ERROR',
    Reset = 'RESET',
}

export const CHUNKED_REVEAL_CONSTANTS = {
    DEFAULT_DELAY_MS: 500,
} as const;

export const QUERY_CONSTANTS = {
    CONVERSATIONS_STALE_TIME_MS: 1000 * 60 * 5, // 5 minutes
    CONVERSATIONS_GC_TIME_MS: 1000 * 60 * 30, // 30 minutes
} as const;

export const STREAMING_CONSTANTS = {
    TIMEOUT_MS: 300000, // 5 minute timeout
    GENERATING_RESPONSE_TIMEOUT_MS: 20 * 60 * 1000, // 10 minutes — matches server-side generation_in_progress window
} as const;

// Streaming-specific event types (extends content types)
export const STREAMING_EVENT_TYPES = {
    TEXT: 'text',
    THINKING: 'thinking',
    CHART: 'chart',
    TABLE: 'table',
    TOOL_USE: 'tool_use',
    TOOL_RESULT: 'tool_result',
    SQL_EXECUTING: 'sql_executing',
    THREAD_METADATA: 'thread_metadata',
    FETCHED_TABLE: 'fetched_table',
    METADATA: 'metadata',
    STATUS: 'status',
    DELTA_CONTENT: 'delta_content',
    CONTENT_ARRAY: 'content_array',
    DONE: 'done',
    ERROR: 'error',
    SQL: 'sql',
    ANSWER_PROCESSING: 'answer_processing',
    DOCUMENT_ATTACHMENT: 'document_attachment',
    AGENT_OPTIONS: 'agent_options',
    OPTION_SELECTED: 'option_selected',
    PLAN_SUMMARY: 'plan_summary',
    PLAN_PROGRESS: 'plan_progress',
    REPORT_UPDATED: 'report_updated',
    REPORT_PROGRESS: 'report_progress',
    // Reels agent blocks (src/lib/reel-studio/agent/blocks.ts, REELS_BLOCK_EVENTS).
    VIDEO_PROGRESS: 'video_progress',
    VIDEO_THEMES: 'video_themes',
    CLIP: 'clip',
    /** Not a block: the video study the conversation is now about. */
    VIDEO_STUDY: 'video_study',
} as const;

export const ROLE_TYPES = {
    USER: 'user',
    ASSISTANT: 'assistant',
    SYSTEM: 'system',
} as const;

export const SQL_EXECUTION_STATUS = {
    EXECUTING: 'executing',
    COMPLETED: 'completed',
    ERROR: 'error',
} as const;

/**
 * Word replacements to apply to DANI responses
 * Format: [original word, replacement word]
 * Note: The replacement logic handles plurals and case variations automatically
 */
export const WORD_REPLACEMENTS: ReadonlyArray<readonly [string, string]> = [
    ['panelist', 'consumer'],
] as const;

// MIME types mapping for allowed document types
export const MIME_TYPES: Record<string, string> = {
    pdf: 'application/pdf',
    csv: 'text/csv',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    txt: 'text/plain',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    doc: 'application/msword',
};

export enum AgentModes {
    PLANNER = 'planner',
    EXPERIMENTAL = 'experimental',
}

export const INITIAL_VISIBLE_COUNT = 3;

export const IS_NEW_THRESHOLD_DAYS = 7;

export const STEP_CONFIG_TOOLTIPS = {
    stepId: 'Unique identifier for this step. Used as the key in the agent instructions highlighted as {{brackets}}. Lowercase, snake_case recommended.',
    allowSkip: 'When on, the user is allowed to skip this step without providing an answer.',
    allowCustom:
        'When on, the user can type a custom free-form answer in addition to (or instead of) the provided options.',
    autoPopulate:
        'When on, the planner agent auto-generates the options list via the suggest_options tool instead of asking the user to type the answer, then renders the result for the user to edit or confirm.',
    allowMultiple: 'When on, the user can pick more than one option for this step.',
} as const;

export const STEP_CONFIG_TYPES: Array<{ value: PlanTemplateStepType; label: string }> = [
    { value: 'text', label: 'Text' },
    { value: 'text_list', label: 'Text List' },
    { value: 'select', label: 'Select (single choice)' },
    { value: 'multi_select', label: 'Multi-Select (many choices)' },
];
